import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Card, CardBody, Input, UncontrolledTooltip } from "reactstrap";
import axios from "axios";
import { Line } from "react-chartjs-2";
import TinyWeekRangePicker from "../../components/tiny/TinyWeekRangePicker";
import GrowthBadge from "../../components/tiny/GrowthBadge";
import { windowNav, previousWindow } from "../../components/tiny/weekWindowUtils";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";
import TinyLoading, { TinyLoadingOverlay } from "components/tiny/TinyLoading";
import TinyPageHeader from "../../components/tiny/TinyPageHeader";

const API_BASE = "/api/tiny";
const COLORS = {
  cardBg: "#1e2530",
  headerBg: "#252d3a",
  inputBg: "#1a1f2b",
  softBg: "rgba(255,255,255,0.04)",
  border: "rgba(255,255,255,0.1)",
  borderLight: "rgba(255,255,255,0.05)",
  borderInput: "rgba(255,255,255,0.2)",
  text: "#fff",
  textSub: "rgba(255,255,255,0.8)",
  green: "#2dce89",
  cyan: "#11cdef",
  purple: "#667eea",
  orange: "#fb6340",
};
const TABLE_TABS = [
  { key: "summary", label: "總覽", icon: "fa fa-th-list" },
  { key: "weekly", label: "逐周查看", icon: "fa fa-calendar" },
  { key: "chart", label: "趨勢圖表", icon: "fa fa-line-chart" },
];
const DATA_MODES = [
  { key: "total", label: "總和" },
  { key: "nlh_plo", label: "NLH/PLO" },
  { key: "aof", label: "AOF" },
  { key: "sng", label: "SNG" },
  { key: "mtt", label: "MTT" },
];
const CHART_PALETTE = [
  "#1f8ef1", "#00d6b4", "#fd5d93", "#f17e5d", "#e14eca",
  "#ffd600", "#11cdef", "#2dce89", "#fb6340", "#667eea",
  "#9A9A9A", "#ff8a80", "#69f0ae", "#b388ff", "#ff80ab",
];

const thStyle = { background: COLORS.headerBg, color: COLORS.text, fontWeight: 600, borderColor: COLORS.border, fontSize: "12px", whiteSpace: "nowrap", padding: "10px 12px", position: "sticky", top: 0, zIndex: 2 };
const tdStyle = { borderColor: COLORS.borderLight, color: COLORS.textSub, verticalAlign: "middle", padding: "8px 12px", fontSize: "13px", whiteSpace: "nowrap" };
const selectStyle = {
  background: COLORS.inputBg,
  color: COLORS.text,
  border: `1px solid ${COLORS.borderInput}`,
  borderRadius: "6px",
  fontWeight: 600,
  fontSize: "13px",
  height: "34px",
  lineHeight: "20px",
  paddingTop: "4px",
  paddingBottom: "4px",
};


function formatNumber(v, digits = 0) {
  return Number(v || 0).toLocaleString("zh-TW", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
function formatInt(v) {
  return Number(v || 0).toLocaleString("zh-TW", { maximumFractionDigits: 0 });
}

function TinyBusinessAnalytics() {
  const [weeks, setWeeks] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [displayedWeeks, setDisplayedWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");

  const [includeReal, setIncludeReal] = useState(true);
  const [includeEcobot, setIncludeEcobot] = useState(true);
  // v2.28.0 自營Bot：我方自己的開桌機器人。預設開 = 跟改版前「混在真人裡」的總量一致。
  const [includeSelfBot, setIncludeSelfBot] = useState(true);
  const [entityType, setEntityType] = useState("clubs");
  const [dataMode, setDataMode] = useState("total");
  const [activeTab, setActiveTab] = useState("summary");
  const [chartMetric, setChartMetric] = useState("rake");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [overview, setOverview] = useState({ summary: {}, by_game_type: {} });
  const [summaryRows, setSummaryRows] = useState([]);
  const [prevSummaryRows, setPrevSummaryRows] = useState([]);
  const [weeklyRows, setWeeklyRows] = useState([]);
  const [trendData, setTrendData] = useState([]);

  const [summarySort, setSummarySort] = useState({ field: "rake", direction: "desc" });
  const [weeklySort, setWeeklySort] = useState({ field: "rake", direction: "desc" });

  const chartRef = useRef(null);
  const [chartBtnFlash, setChartBtnFlash] = useState("");
  const [chartBtnHover, setChartBtnHover] = useState("");

  const loadWeeks = useCallback(async () => {
    const res = await axios.get(`${API_BASE}/data/weeks`);
    if (res.data.code !== 1) throw new Error(res.data.msg || "載入週期失敗");
    const allWeeks = res.data.data.weeks || [];
    const defaults = allWeeks.slice(0, Math.min(1, allWeeks.length));
    setWeeks(allWeeks);
    setSelectedWeeks(defaults);
    setDisplayedWeeks(defaults);
    setSelectedWeek(defaults[0] || "");
  }, []);

  const baseParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append("include_real", includeReal ? "1" : "0");
    params.append("include_ecobot", includeEcobot ? "1" : "0");
    params.append("include_self_bot", includeSelfBot ? "1" : "0");
    params.append("entity_type", entityType);
    params.append("data_mode", dataMode);
    return params;
  }, [includeEcobot, includeReal, includeSelfBot, entityType, dataMode]);

  const fetchOverview = useCallback(async () => {
    if (!displayedWeeks.length) return;
    const params = new URLSearchParams(baseParams);
    params.append("weeks", displayedWeeks.join(","));
    params.append("include_breakdown", "1");
    const res = await axios.get(`${API_BASE}/analytics/b-end-overview?${params.toString()}`);
    if (res.data.code !== 1) throw new Error(res.data.msg || "載入總覽失敗");
    setOverview(res.data.data || { summary: {}, by_game_type: {} });
  }, [baseParams, displayedWeeks]);

  const fetchSummaryRows = useCallback(async () => {
    if (!displayedWeeks.length) return;
    const params = new URLSearchParams(baseParams);
    params.append("weeks", displayedWeeks.join(","));
    params.append("metric", "rake");
    params.append("game_type", dataMode);
    params.append("limit", "200");
    const res = await axios.get(`${API_BASE}/analytics/b-end-breakdown?${params.toString()}`);
    if (res.data.code !== 1) throw new Error(res.data.msg || "載入總覽明細失敗");
    const data = res.data.data || {};
    setSummaryRows(data.rows || []);
  }, [baseParams, dataMode, displayedWeeks]);

  // 上一期（前 N 週）總覽明細，供「與上一期比較」成長%
  const fetchPrevSummaryRows = useCallback(async () => {
    const prev = previousWindow(weeks, displayedWeeks);
    if (!prev || !prev.length) {
      setPrevSummaryRows([]);
      return;
    }
    const params = new URLSearchParams(baseParams);
    params.append("weeks", prev.join(","));
    params.append("metric", "rake");
    params.append("game_type", dataMode);
    params.append("limit", "200");
    const res = await axios.get(`${API_BASE}/analytics/b-end-breakdown?${params.toString()}`);
    if (res.data.code !== 1) {
      setPrevSummaryRows([]);
      return;
    }
    setPrevSummaryRows((res.data.data || {}).rows || []);
  }, [baseParams, dataMode, displayedWeeks, weeks]);

  const fetchWeeklyRows = useCallback(async () => {
    if (!selectedWeek) return;
    const params = new URLSearchParams(baseParams);
    params.append("weeks", selectedWeek);
    params.append("metric", "rake");
    params.append("game_type", dataMode);
    params.append("limit", "200");
    const res = await axios.get(`${API_BASE}/analytics/b-end-breakdown?${params.toString()}`);
    if (res.data.code !== 1) throw new Error(res.data.msg || "載入逐周資料失敗");
    setWeeklyRows((res.data.data || {}).rows || []);
  }, [baseParams, dataMode, selectedWeek]);

  const fetchTrend = useCallback(async () => {
    if (!displayedWeeks.length) return;
    const sorted = [...displayedWeeks].sort();
    const results = await Promise.all(
      sorted.map(async (week) => {
        const params = new URLSearchParams(baseParams);
        params.append("weeks", week);
        params.append("metric", "rake");
        params.append("game_type", dataMode);
        params.append("limit", "200");
        const res = await axios.get(`${API_BASE}/analytics/b-end-breakdown?${params.toString()}`);
        if (res.data.code !== 1) throw new Error(res.data.msg || "載入趨勢失敗");
        return { week, rows: (res.data.data || {}).rows || [] };
      })
    );
    setTrendData(results);
  }, [baseParams, dataMode, displayedWeeks]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadWeeks();
      } catch (err) {
        setError(err.message || "初始化失敗");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadWeeks]);

  useEffect(() => {
    (async () => {
      if (!displayedWeeks.length) return;
      try {
        setLoading(true);
        setError("");
        await fetchOverview();
        await fetchSummaryRows();
        await fetchPrevSummaryRows();
        if (activeTab === "weekly") await fetchWeeklyRows();
        if (activeTab === "chart") await fetchTrend();
      } catch (err) {
        setError(err.message || "載入失敗");
      } finally {
        setLoading(false);
      }
    })();
  }, [activeTab, fetchOverview, fetchSummaryRows, fetchPrevSummaryRows, fetchTrend, fetchWeeklyRows, displayedWeeks]);

  const handleSearch = () => {
    setDisplayedWeeks([...selectedWeeks]);
    if (selectedWeeks.length > 0) setSelectedWeek(selectedWeeks[0]);
  };

  // 上/下 N 週：直接套用移動後的視窗（免再送出），並同步選擇器勾選
  const applyWindow = (win) => {
    if (!win || !win.length) return;
    setSelectedWeeks(win);
    setDisplayedWeeks(win);
    setSelectedWeek(win[0]);
  };

  const columns = useMemo(() => {
    const base = [
      // 名稱與 ID 併成一欄（ID 以小字置於名稱下方），省一欄寬度
      // 註：原本的 "#" 序號欄已移除 —— 純裝飾、佔寬度，拿掉才不用左右捲動
      { key: "entity_name", label: entityType === "clubs" ? "俱樂部" : "直營超代/代理",
        tooltip: "名稱（上）與 ID（下）" },
      { key: "type", label: "類型" },
      { key: "rake", label: "Rake", numeric: true },
      { key: "post_rake_pl", label: "水後輸贏", numeric: true },
      { key: "pre_rake_pl", label: "水前輸贏", numeric: true },
      { key: "rg_pre_rake_pl", label: "RG 水前", numeric: true,
        tooltip: "RG（含 AOF）水前輸贏，排除 SNG/MTT。對齊 rebate 與雙週獎池公式範圍。" },
    ];
    if (dataMode === "total" || dataMode === "nlh_plo") {
      // JP Fee / Payout 併成一欄：淨值為主，兩個組成以小字分色列於下方
      base.push({ key: "jp_net", label: "JP 淨值", numeric: true, sortable: false,
        tooltip: "JP Fee − JP Payout。上方粗體為淨值；下方小字青綠＝Fee（聯盟收）、橘＝Payout（聯盟付）。所選週期跨多週時為加總。" });
      base.push({ key: "insurance", label: "真實保險", numeric: true,
        tooltip: "該 B 端當週玩家保險淨額（members_weekly insurance.total 加總，已依系統慣例 ×-1：正＝保險池淨收、負＝淨付）。此為真實數據；分潤後實際給 B 端的「保險分潤」請見每週交收總覽。所選週期跨多週時為加總。" });
      base.push({ key: "pre_rake_pl_ex_jp", label: "RG 水前(扣JP)", numeric: true,
        tooltip: "RG（含 AOF）水前輸贏扣 JP。純 Rebate 模式：× rebate% 即為 Rebate 金額；TR 則以此為分子算 ratio 後命中級距。" });
    }
    if (dataMode === "total") {
      base.push({ key: "leaderboard", label: "LB", numeric: true,
        tooltip: "Leaderboard 獎金" });
    }
    base.push({
      key: "active_players", label: "活躍人數", numeric: true, integer: true,
      tooltip: {
        summary: "所選多個週期內，曾產生任何 Rake 的不重複玩家總數。跨週去重：同一位玩家即使每週都有活動，仍只計算一次。",
        weekly: "該單週內，曾產生任何 Rake 的不重複玩家數量。僅針對該週資料去重，不涉及其他週。",
      },
    });
    base.push({
      key: "rebate", label: "純 Rebate", numeric: true, sortable: false,
      tooltip: "v2.1.0 純 Rebate 模式：max(0, −水前輸贏) × 固定 rate%，取代分潤+獎池。非 Rebate 模式顯示「—」。所選週期跨多週時為加總。",
    });
    // v2.26.0：TR 與純 Rebate 並排，方便對比（兩者是獨立機制，可能同時存在）
    base.push({
      key: "tr", label: "TR", numeric: true, sortable: false,
      tooltip: "v2.25.0 Tax & Rebate：ratio = RG 水前(扣JP) ÷ RG 總水，命中級距後算出金額。本欄與「JP 淨值」同慣例「我方收為正」：青綠正數＝Tax、橘色負數＝Rebate。⚠️ Excel 與每週交收總覽沿用交收慣例（正＝我方應付對方），正負號與本欄相反，對帳時請留意。僅計 Ring Game（NLH / PLO / AOF），不含 MTT / SNG。不適用或未生效顯示「—」。",
    });
    return base;
  }, [dataMode, entityType]);

  const mapRowForMode = useCallback((row) => {
    if (dataMode === "total") return row;
    const g = (row.by_game_type || {})[dataMode] || {};
    const rake = Number(g.rake || 0);
    const pl = Number(g.post_rake_pl || 0);
    return {
      ...row,
      rake,
      post_rake_pl: pl,
      pre_rake_pl: rake + pl,
      active_players: g.active_players || 0,
      pre_rake_pl_ex_jp: rake + pl + Number(row.jp_fee || 0) - Number(row.jp_payout || 0),
      leaderboard: 0,
    };
  }, [dataMode]);

  const sortRows = useCallback((rows, sortState) => {
    const list = rows.map(mapRowForMode);
    const { field, direction } = sortState;
    if (!field) return list;
    return [...list].sort((a, b) => {
      const av = a[field];
      const bv = b[field];
      if (typeof av === "number" && typeof bv === "number") {
        return direction === "asc" ? av - bv : bv - av;
      }
      return direction === "asc"
        ? String(av || "").localeCompare(String(bv || ""))
        : String(bv || "").localeCompare(String(av || ""));
    });
  }, [mapRowForMode]);

  const sortedSummaryRows = useMemo(() => sortRows(summaryRows, summarySort), [summaryRows, summarySort, sortRows]);
  const sortedWeeklyRows = useMemo(() => sortRows(weeklyRows, weeklySort), [weeklyRows, weeklySort, sortRows]);

  // 上一期 Rake 對照（by entity_id）+ Rake 趨勢數字（本期 ΣRake vs 上期 ΣRake）
  const prevRakeMap = useMemo(() => {
    const m = new Map();
    prevSummaryRows.forEach((r) => m.set(r.entity_id, mapRowForMode(r)));
    return m;
  }, [prevSummaryRows, mapRowForMode]);

  const rakeTrend = useMemo(() => {
    const cur = sortedSummaryRows.reduce((a, r) => a + Number(r.rake || 0), 0);
    const prev = prevSummaryRows.reduce((a, r) => a + Number(mapRowForMode(r).rake || 0), 0);
    return { cur, prev: prevSummaryRows.length ? prev : null };
  }, [sortedSummaryRows, prevSummaryRows, mapRowForMode]);

  const toggleSort = (current, setter, field) => {
    setter((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { field, direction: "desc" };
    });
  };

  const renderHeader = (sortState, setSortState) => {
    const isSummary = sortState === summarySort;
    const ctx = isSummary ? "s" : "w";
    return (
      <thead>
        <tr>
          {columns.map((c) => {
            const active = sortState.field === c.key;
            const align = c.numeric ? "right" : "left";
            const tipText = c.tooltip ? (typeof c.tooltip === "string" ? c.tooltip : c.tooltip[isSummary ? "summary" : "weekly"]) : null;
            const tipId = tipText ? `tip-${c.key}-${ctx}` : null;
            return (
              <th
                key={c.key}
                style={{ ...thStyle, cursor: c.sortable === false ? "default" : "pointer", userSelect: "none", textAlign: align }}
                onClick={c.sortable === false ? undefined : () => toggleSort(sortState, setSortState, c.key)}
              >
                {tipText ? (
                  <span id={tipId} style={{ cursor: "help" }}>
                    {c.label} <i className="fa fa-info-circle" style={{ fontSize: "10px", opacity: 0.5 }} />
                  </span>
                ) : (
                  c.label
                )}{" "}
                {c.sortable !== false && (
                  <span style={{ color: active ? "#667eea" : "rgba(255,255,255,0.3)", fontSize: "10px" }}>
                    {active ? (sortState.direction === "desc" ? "\u25BC" : "\u25B2") : "\u25BC"}
                  </span>
                )}
                {tipId && (
                  <UncontrolledTooltip placement="top" target={tipId} style={{ maxWidth: 300, textAlign: "left" }}>
                    {tipText}
                  </UncontrolledTooltip>
                )}
              </th>
            );
          })}
        </tr>
      </thead>
    );
  };

  const renderTotalRow = (rows) => {
    const sum = (key) => rows.reduce((acc, r) => acc + Number(r[key] || 0), 0);
    const rebateTotal = rows.reduce((acc, r) => acc + Number((r.rebate && r.rebate.amount) || 0), 0);
    const totalTd = {
      ...tdStyle,
      background: COLORS.headerBg,
      color: COLORS.text,
      fontWeight: 700,
      borderTop: `2px solid ${COLORS.borderInput}`,
    };
    return (
      <tr>
        {columns.map((c) => {
          if (c.key === "entity_name") return <td key={c.key} style={totalTd}>{`Σ 總和（${rows.length} 筆）`}</td>;
          if (c.key === "entity_id" || c.key === "type") return <td key={c.key} style={totalTd} />;
          if (c.key === "jp_net") {
            const feeSum = sum("jp_fee");
            const paySum = sum("jp_payout");
            return (
              <td key={c.key} style={{ ...totalTd, textAlign: "right", fontFamily: "monospace" }}>
                <div>{formatNumber(feeSum - paySum)}</div>
                <small style={{ fontSize: "10px", fontWeight: 400, whiteSpace: "nowrap" }}>
                  <span style={{ color: "#4bc0c0" }}>+{formatNumber(feeSum)}</span>
                  <span style={{ color: "#6b7280" }}> / </span>
                  <span style={{ color: "#ff922b" }}>-{formatNumber(paySum)}</span>
                </small>
              </td>
            );
          }
          if (c.key === "tr") {
            // 合計同時給淨額與兩側，避免正負互相抵消後看不出規模。
            // 與資料列一致：翻轉成「我方收為正」（Tax 正 / Rebate 負），排序比照 JP 淨值（青綠在前）
            const trRows = rows.map((r) => Number((r.tr && r.tr.amount) || 0));
            const trNetShown = -trRows.reduce((a, b) => a + b, 0);
            const taxShown = -trRows.filter((v) => v < 0).reduce((a, b) => a + b, 0);   // 正
            const rebateShown = -trRows.filter((v) => v > 0).reduce((a, b) => a + b, 0); // 負
            return (
              <td key={c.key} style={{ ...totalTd, textAlign: "right", fontFamily: "monospace" }}>
                {taxShown || rebateShown ? (
                  <>
                    <div>{formatNumber(trNetShown)}</div>
                    <small style={{ fontSize: "10px", fontWeight: 400, whiteSpace: "nowrap" }}>
                      <span style={{ color: "#4bc0c0" }}>+{formatNumber(taxShown)}</span>
                      <span style={{ color: "#6b7280" }}> / </span>
                      <span style={{ color: "#ff922b" }}>{formatNumber(rebateShown)}</span>
                    </small>
                  </>
                ) : <span style={{ color: "#9aa4bb" }}>—</span>}
              </td>
            );
          }
          if (c.key === "rebate") {
            return (
              <td key={c.key} style={{ ...totalTd, textAlign: "right", fontFamily: "monospace" }}>
                {rebateTotal
                  ? <span style={{ color: "#9b51e0" }}>{formatNumber(rebateTotal)}</span>
                  : <span style={{ color: "#9aa4bb" }}>—</span>}
              </td>
            );
          }
          if (c.numeric) {
            const v = sum(c.key);
            const isPL = ["post_rake_pl", "pre_rake_pl", "rg_pre_rake_pl", "pre_rake_pl_ex_jp", "insurance"].includes(c.key);
            const color = isPL ? (v > 0 ? "#2dce89" : v < 0 ? "#f5365c" : COLORS.text) : COLORS.text;
            return (
              <td key={c.key} style={{ ...totalTd, textAlign: "right", fontFamily: "monospace", color }}>
                {c.integer ? formatInt(v) : formatNumber(v)}
              </td>
            );
          }
          return <td key={c.key} style={totalTd} />;
        })}
      </tr>
    );
  };

  const renderRows = (rows, prevMap = null) => (
    <tbody>
      {rows.length ? (
        <>
        {rows.map((row, idx) => {
        const isBot = row.type === "ECOBOT";
        // v2.28.0 自營Bot 用青綠底色，跟橘色的 EcoBot 一眼分得開
        const isSelfBot = row.type === "自營BOT";
        const rowBg = isBot ? "rgba(251,99,64,0.08)"
          : isSelfBot ? "rgba(0,214,180,0.10)" : "transparent";
        return (
          <tr key={`${row.entity_id}-${idx}`} style={{ background: rowBg }}>
            {columns.map((c) => {
              const raw = c.render ? c.render(row, idx) : row[c.key];
              const text = c.integer ? formatInt(raw) : c.numeric ? formatNumber(raw) : (raw || "-");
              const isPL = c.numeric && ["post_rake_pl", "pre_rake_pl", "rg_pre_rake_pl", "pre_rake_pl_ex_jp", "insurance"].includes(c.key);
              const plColor = Number(raw || 0) > 0 ? "#2dce89" : Number(raw || 0) < 0 ? "#f5365c" : COLORS.textSub;

              // 名稱 + ID 併一欄：ID 以小字灰色置於名稱下方
              if (c.key === "entity_name") {
                return (
                  <td key={c.key} style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{row.entity_name || "-"}</div>
                    <small style={{ color: "#9aa4bb", fontSize: "10px", fontFamily: "monospace" }}>
                      {row.entity_id || "-"}
                    </small>
                  </td>
                );
              }

              // JP Fee / Payout 併一欄：淨值為主，組成以小字分色（不用紅綠）
              if (c.key === "jp_net") {
                const fee = Number(row.jp_fee || 0);
                const payout = Number(row.jp_payout || 0);
                const net = fee - payout;
                if (!fee && !payout) {
                  return (
                    <td key={c.key} style={{ ...tdStyle, textAlign: "right", color: "#9aa4bb", fontFamily: "monospace" }}>—</td>
                  );
                }
                return (
                  <td key={c.key} style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>
                    <div style={{ fontWeight: 700, color: COLORS.text }}>{formatNumber(net)}</div>
                    <small style={{ fontSize: "10px", whiteSpace: "nowrap" }}>
                      <span style={{ color: "#4bc0c0" }}>+{formatNumber(fee)}</span>
                      <span style={{ color: "#6b7280" }}> / </span>
                      <span style={{ color: "#ff922b" }}>-{formatNumber(payout)}</span>
                    </small>
                  </td>
                );
              }

              // TR：正＝Rebate（我方付）、負＝Tax（我方收）。與純 Rebate 分色，避免混淆
              if (c.key === "tr") {
                const tr = row.tr;
                if (!tr || (!tr.amount && !tr.kind)) {
                  return (
                    <td key={c.key} style={{ ...tdStyle, textAlign: "right", color: "#9aa4bb", fontFamily: "monospace" }}>—</td>
                  );
                }
                const amt = Number(tr.amount || 0);
                // ⚠️ 顯示用正負號與 DB 相反：
                //   DB / Excel / 交收 snapshot 沿用交收慣例「正 = 我方應付對方」→ Rebate 正、Tax 負。
                //   但本表同列的「JP 淨值」是「我方收為正」，兩套並排會誤判，
                //   故此欄一律翻轉成「我方收為正」：Tax 正、Rebate 負。**只影響顯示，不動資料。**
                const isTax = amt < 0;
                const shown = -amt;                       // 翻轉：我方收為正
                const trColor = isTax ? "#4bc0c0" : "#ff922b";
                return (
                  <td key={c.key} style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>
                    <div style={{ color: trColor, fontWeight: 700 }}>{formatNumber(shown)}</div>
                    <small style={{ color: trColor, fontSize: "10px", opacity: 0.75 }}>
                      {isTax ? "Tax" : "Rebate"}
                    </small>
                  </td>
                );
              }

              if (c.key === "type") {
                return (
                  <td key={c.key} style={tdStyle}>
                    {raw ? (
                      <span style={{
                        background: isBot ? "linear-gradient(135deg, #fb6340, #fbb140)" : "linear-gradient(135deg, #1f8ef1, #0d6efd)",
                        color: "#fff", fontSize: "10px", fontWeight: 600,
                        padding: "2px 8px", borderRadius: "10px", letterSpacing: "0.5px",
                      }}>{raw}</span>
                    ) : "-"}
                  </td>
                );
              }

              if (c.key === "rebate") {
                const rebate = row.rebate;
                if (!rebate) {
                  return (
                    <td key={c.key} style={{ ...tdStyle, textAlign: "right", color: "#9aa4bb", fontFamily: "monospace" }}>
                      —
                    </td>
                  );
                }
                return (
                  <td key={c.key} style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>
                    <div style={{ color: "#9b51e0", fontWeight: 700 }}>{formatNumber(rebate.amount || 0)}</div>
                    {rebate.rate != null && (
                      <small style={{ color: "#c79afc", fontSize: "10px" }}>Rebate {rebate.rate}%</small>
                    )}
                  </td>
                );
              }

              if (c.key === "rake") {
                const prev = prevMap ? prevMap.get(row.entity_id) : null;
                return (
                  <td key={c.key} style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>
                    <div>{text}</div>
                    {prevMap && (
                      <GrowthBadge current={Number(raw || 0)} previous={prev ? Number(prev.rake || 0) : undefined} />
                    )}
                  </td>
                );
              }

              return (
                <td
                  key={c.key}
                  style={{
                    ...tdStyle,
                    textAlign: c.numeric ? "right" : "left",
                    fontFamily: c.numeric ? "monospace" : "inherit",
                    color: isPL ? plColor : tdStyle.color,
                    fontWeight: isPL ? 600 : 400,
                  }}
                >
                  {text}
                </td>
              );
            })}
          </tr>
        );
        })}
        {renderTotalRow(rows)}
        </>
      ) : (
        <tr><td style={tdStyle} colSpan={columns.length} className="text-center">暫無資料</td></tr>
      )}
    </tbody>
  );

  const chartMetricOptions = useMemo(() => {
    const base = [
      { key: "rake", label: "Rake" },
      { key: "post_rake_pl", label: "水後輸贏" },
      { key: "pre_rake_pl", label: "水前輸贏" },
    ];
    if (dataMode === "total" || dataMode === "nlh_plo") {
      base.push({ key: "jp_fee", label: "JP Fee" });
      base.push({ key: "jp_payout", label: "JP Payout" });
      base.push({ key: "insurance", label: "真實保險" });
      base.push({ key: "pre_rake_pl_ex_jp", label: "RG 水前輸贏（扣除 JP）" });
    }
    if (dataMode === "total") {
      base.push({ key: "leaderboard", label: "Leaderboard" });
    }
    base.push({ key: "active_players", label: "活躍人數" });
    return base;
  }, [dataMode]);

  useEffect(() => {
    if (!chartMetricOptions.find((o) => o.key === chartMetric)) {
      setChartMetric("rake");
    }
  }, [chartMetricOptions, chartMetric]);

  const chartData = useMemo(() => {
    if (!trendData.length) return { labels: [], datasets: [] };
    const labels = trendData.map((d) => d.week);
    const entityMap = new Map();
    trendData.forEach(({ week, rows }) => {
      rows.forEach((raw) => {
        const row = mapRowForMode(raw);
        const id = raw.entity_id;
        if (!entityMap.has(id)) {
          entityMap.set(id, { name: raw.entity_name || id, values: {} });
        }
        entityMap.get(id).values[week] = Number(row[chartMetric] || 0);
      });
    });
    const datasets = [...entityMap.entries()].map(([, e], i) => ({
      label: e.name,
      data: labels.map((w) => e.values[w] || 0),
      borderColor: CHART_PALETTE[i % CHART_PALETTE.length],
      backgroundColor: "transparent",
      tension: 0.3,
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
    }));
    return { labels, datasets };
  }, [trendData, chartMetric, mapRowForMode]);

  const handleShowAll = useCallback(() => {
    const c = chartRef.current;
    if (c) { c.data.datasets.forEach((_, i) => c.setDatasetVisibility(i, true)); c.update(); }
    setChartBtnFlash("show");
    setTimeout(() => setChartBtnFlash(""), 400);
  }, []);
  const handleHideAll = useCallback(() => {
    const c = chartRef.current;
    if (c) { c.data.datasets.forEach((_, i) => c.setDatasetVisibility(i, false)); c.update(); }
    setChartBtnFlash("hide");
    setTimeout(() => setChartBtnFlash(""), 400);
  }, []);

  const trendChartOpts = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        labels: { color: "rgba(255,255,255,0.8)", usePointStyle: true, pointStyle: "circle", padding: 14, font: { size: 11 } },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(0,0,0,0.85)",
        titleColor: "#fff",
        bodyColor: "rgba(255,255,255,0.8)",
        borderColor: "rgba(255,255,255,0.1)",
        borderWidth: 1,
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.raw)}` },
      },
    },
    scales: {
      x: { ticks: { color: "#9a9a9a", font: { size: 10 } }, grid: { color: "rgba(255,255,255,0.05)" } },
      y: { ticks: { color: "#9a9a9a", font: { size: 10 }, callback: (v) => formatNumber(v) }, grid: { color: "rgba(255,255,255,0.05)" } },
    },
  }), []);

  const nav = windowNav(weeks, displayedWeeks);

  // 目前這個 tab 畫面上已經有資料時，重抓（切週 / 切 tab / 切篩選）只蓋遮罩、舊資料留著
  const hasTabData =
    activeTab === "summary" ? summaryRows.length > 0
    : activeTab === "weekly" ? weeklyRows.length > 0
    : trendData.length > 0;

  return (
    <div className="content">
      {error ? <Alert color="danger" toggle={() => setError("")}>{error}</Alert> : null}

      {/* ⚠️ 這一頁的檔名叫 TinyBusinessAnalytics，但 routes.js 註冊的是
             `TinyClubRakeContainer` —— 名字對不起來，容易改錯檔。
             （`views/tiny/TinyClubRake.js` 沒有任何人 import，是死檔。）
          範圍控制（可多選週期 + 前後窗格導覽）進 header，用 dropdown 模式；
          原本的 panel 模式是一整塊清單，光週期就佔掉半個畫面。
          🚫 真人／ECOBOT／自營BOT 開關、遊戲種類、查看模式**不進 header** ——
             它們是「這份資料怎麼切」的 filter，跟資料放在一起才讀得懂。 */}
      <TinyPageHeader
        title="B端數據分析"
        description="各俱樂部與直營超代／代理的 Rake 表現，可多選週期比較。"
        scope={
          <TinyWeekRangePicker
            options={weeks}
            selectedValues={selectedWeeks}
            onChange={setSelectedWeeks}
            onApply={handleSearch}
            onCancel={() => setSelectedWeeks([...displayedWeeks])}
            nav={nav}
            onWindowChange={applyWindow}
          />
        }
        tabs={{
          items: TABLE_TABS.map((t) => ({ key: t.key, label: t.label, icon: t.icon })),
          active: activeTab,
          onChange: setActiveTab,
        }}
      />

      {/* 資料切法的控制列 —— 跟它作用的資料放在一起，不進 header。 */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        <button
          onClick={() => setIncludeReal((v) => !v)}
          style={{
            background: includeReal ? "linear-gradient(135deg, #2dce89, #26a770)" : "rgba(255,255,255,0.08)",
            border: includeReal ? "none" : "1px solid rgba(255,255,255,0.2)",
            color: includeReal ? "#fff" : "rgba(255,255,255,0.6)",
            padding: "5px 14px", borderRadius: "6px", fontSize: "12px", fontWeight: 600,
            cursor: "pointer", transition: "all 0.2s",
            boxShadow: includeReal ? "0 3px 8px rgba(0,0,0,0.2)" : "none",
          }}
        >真人俱樂部</button>
        <button
          onClick={() => setIncludeEcobot((v) => !v)}
          style={{
            background: includeEcobot ? "linear-gradient(135deg, #fb6340, #fbb140)" : "rgba(255,255,255,0.08)",
            border: includeEcobot ? "none" : "1px solid rgba(255,255,255,0.2)",
            color: includeEcobot ? "#fff" : "rgba(255,255,255,0.6)",
            padding: "5px 14px", borderRadius: "6px", fontSize: "12px", fontWeight: 600,
            cursor: "pointer", transition: "all 0.2s",
            boxShadow: includeEcobot ? "0 3px 8px rgba(0,0,0,0.2)" : "none",
          }}
        >ECOBOT</button>
        {/* v2.28.0 自營Bot：我方自己研發的開桌機器人，跟 EcoBot（外部夥伴）是兩回事 */}
        <button
          onClick={() => setIncludeSelfBot((v) => !v)}
          style={{
            background: includeSelfBot ? "linear-gradient(135deg, #00d6b4, #0d9488)" : "rgba(255,255,255,0.08)",
            border: includeSelfBot ? "none" : "1px solid rgba(255,255,255,0.2)",
            color: includeSelfBot ? "#fff" : "rgba(255,255,255,0.6)",
            padding: "5px 14px", borderRadius: "6px", fontSize: "12px", fontWeight: 600,
            cursor: "pointer", transition: "all 0.2s",
            boxShadow: includeSelfBot ? "0 3px 8px rgba(0,0,0,0.2)" : "none",
          }}
        >自營BOT</button>
        <span style={{ color: "rgba(255,255,255,0.25)", margin: "0 4px" }}>|</span>
        <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px", fontWeight: 500 }}>遊戲種類</span>
        <Input type="select" value={dataMode} onChange={(e) => setDataMode(e.target.value)} style={{ ...selectStyle, width: "auto", minWidth: 110 }}>
          {DATA_MODES.map((m) => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </Input>
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px", marginLeft: 8 }}>
          顯示 {displayedWeeks.length} 週
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 500, display: "flex", alignItems: "center", gap: 5 }}>
          <i className="fa fa-eye" style={{ fontSize: 11 }} />
          查看模式:
        </span>
        <div style={{ background: "#111822", borderRadius: 6, padding: 3, display: "flex", border: `1px solid ${COLORS.border}` }}>
          {[
            { key: "clubs", label: "俱樂部" },
            { key: "direct_agents", label: "直營超代/直營代理" },
          ].map((opt) => {
            const active = entityType === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setEntityType(opt.key)}
                style={{
                  background: active ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
                  border: "none",
                  color: active ? "#fff" : "rgba(255,255,255,0.55)",
                  padding: "5px 16px", borderRadius: 5, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", transition: "all 0.2s",
                  boxShadow: active ? "0 2px 6px rgba(102,126,234,0.35)" : "none",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <Card className="mt-3" style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
        <CardBody>
          {loading && !hasTabData ? (
            <TinyLoading variant="block" text="載入中" />
          ) : (
            <TinyLoadingOverlay active={loading} text="更新中">
              {activeTab === "summary" && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                    <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, fontWeight: 600 }}>
                      <i className="fa fa-line-chart mr-2" style={{ color: "#1f8ef1" }} />Rake 趨勢（較上一期）
                    </span>
                    <span style={{ fontFamily: "monospace", color: COLORS.text, fontWeight: 700 }}>
                      {formatNumber(rakeTrend.cur)}
                    </span>
                    <GrowthBadge current={rakeTrend.cur} previous={rakeTrend.prev ?? undefined} size="0.95em" />
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      {renderHeader(summarySort, setSummarySort)}
                      {renderRows(sortedSummaryRows, prevRakeMap)}
                    </table>
                  </div>
                </>
              )}

              {activeTab === "weekly" && (
                <>
                  <div className="mb-3">
                    <TinyWeekSelector options={displayedWeeks} value={selectedWeek} onChange={setSelectedWeek} />
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      {renderHeader(weeklySort, setWeeklySort)}
                      {renderRows(sortedWeeklyRows)}
                    </table>
                  </div>
                </>
              )}

              {activeTab === "chart" && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                    <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 500 }}>趨勢指標</span>
                    <Input
                      type="select"
                      value={chartMetric}
                      onChange={(e) => setChartMetric(e.target.value)}
                      style={{ ...selectStyle, width: "auto", minWidth: 140 }}
                    >
                      {chartMetricOptions.map((m) => (
                        <option key={m.key} value={m.key}>{m.label}</option>
                      ))}
                    </Input>
                    <span style={{ color: "rgba(255,255,255,0.2)", margin: "0 2px" }}>|</span>
                    <button
                      onClick={handleShowAll}
                      onMouseEnter={() => setChartBtnHover("show")}
                      onMouseLeave={() => setChartBtnHover("")}
                      style={{
                        background: chartBtnFlash === "show" ? "linear-gradient(135deg, #2dce89, #26a770)"
                          : chartBtnHover === "show" ? "rgba(45,206,137,0.18)" : "rgba(255,255,255,0.08)",
                        border: chartBtnFlash === "show" ? "1px solid rgba(45,206,137,0.6)"
                          : chartBtnHover === "show" ? "1px solid rgba(45,206,137,0.4)" : `1px solid ${COLORS.border}`,
                        color: chartBtnFlash === "show" ? "#fff"
                          : chartBtnHover === "show" ? "#2dce89" : "rgba(255,255,255,0.7)",
                        padding: "4px 12px", borderRadius: 5,
                        fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.25s",
                        boxShadow: chartBtnFlash === "show" ? "0 2px 8px rgba(45,206,137,0.4)" : "none",
                      }}
                    >
                      <i className="fa fa-eye" style={{ marginRight: 4, fontSize: 10 }} />顯示全部
                    </button>
                    <button
                      onClick={handleHideAll}
                      onMouseEnter={() => setChartBtnHover("hide")}
                      onMouseLeave={() => setChartBtnHover("")}
                      style={{
                        background: chartBtnFlash === "hide" ? "linear-gradient(135deg, #f5365c, #f56036)"
                          : chartBtnHover === "hide" ? "rgba(245,54,92,0.18)" : "rgba(255,255,255,0.08)",
                        border: chartBtnFlash === "hide" ? "1px solid rgba(245,54,92,0.6)"
                          : chartBtnHover === "hide" ? "1px solid rgba(245,54,92,0.4)" : `1px solid ${COLORS.border}`,
                        color: chartBtnFlash === "hide" ? "#fff"
                          : chartBtnHover === "hide" ? "#f5365c" : "rgba(255,255,255,0.7)",
                        padding: "4px 12px", borderRadius: 5,
                        fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.25s",
                        boxShadow: chartBtnFlash === "hide" ? "0 2px 8px rgba(245,54,92,0.4)" : "none",
                      }}
                    >
                      <i className="fa fa-eye-slash" style={{ marginRight: 4, fontSize: 10 }} />隱藏全部
                    </button>
                  </div>
                  {chartMetric === "active_players" && (
                    <div style={{
                      background: "rgba(102, 126, 234, 0.1)",
                      border: "1px solid rgba(102, 126, 234, 0.3)",
                      borderRadius: 6, padding: "8px 14px", marginBottom: 12,
                      fontSize: 12, color: "rgba(255,255,255,0.7)", lineHeight: 1.6,
                    }}>
                      <i className="fa fa-info-circle" style={{ color: "#667eea", marginRight: 6 }} />
                      每個資料點為該單週內曾產生任何 Rake 的不重複玩家數量（單週各自獨立去重，不跨週合併）。
                    </div>
                  )}
                  <div style={{ height: 400 }}>
                    <Line ref={chartRef} data={chartData} options={trendChartOpts} />
                  </div>
                </>
              )}
            </TinyLoadingOverlay>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default TinyBusinessAnalytics;
